import { LEVEL_ORDER, getWordLevels, getPhraseForLevel, getPhotoForLevel } from './wordLevels';
// Сборка карточек "фото + фраза" для одного слова из баз пользователей.
// Карточки источников: моя база, базы из моего круга подписок, остальные.

const WORDS_KEY = '__words';
const ORDER_KEY = '__order';

export function collectSearchTerms(word) {
  const terms = new Set();
  const add = (v) => {
    if (typeof v !== "string") return;
    const t = v.trim().toLowerCase();
    if (t) terms.add(t);
  };
  add(word?.text);
  const forms = word?.manualForms || word?.forms;
  if (forms && typeof forms === "object") {
    Object.values(forms).forEach(add);
  }
  return [...terms].sort((a, b) => b.length - a.length);
}

function phraseMatchesTerms(phrase, terms) {
  if (!phrase || !terms.length) return false;
  const lower = String(phrase).toLowerCase();
  // Разбиваем фразу на токены по не-буквенным символам
  const tokens = lower.split(/[^\p{L}\p{N}æøåöäüß-]+/u).filter(Boolean);
  const tokenSet = new Set(tokens);
  return terms.some((t) => tokenSet.has(t));
}

function walkDb(db, onWord) {
  if (!db || typeof db !== "object") return;
  const walk = (node) => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach((w) => onWord(w));
      return;
    }
    if (typeof node !== "object") return;
    if (Array.isArray(node[WORDS_KEY])) {
      node[WORDS_KEY].forEach((w) => onWord(w));
    }
    Object.keys(node).forEach((k) => {
      if (k === WORDS_KEY || k === ORDER_KEY) return;
      walk(node[k]);
    });
  };
  Object.keys(db).forEach((cat) => walk(db[cat]));
}

function extractPhotoTimestamp(photoKey) {
  if (typeof photoKey !== 'string') return 0;
  const m = photoKey.match(/^ph_(\d+)_/);
  return m ? Number(m[1]) : 0;
}

function normEq(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

export function collectDictionaryBaseTexts(db) {
  const set = new Set();
  walkDb(db, (w) => {
    const t = String(w?.text || '').trim().toLowerCase();
    if (t) set.add(t);
  });
  return set;
}

export function collectPhraseCards({ databases, myId, mySubscriptions, targetWord, baseTextSet }) {
  const targetText = String(targetWord?.text || '').trim().toLowerCase();
  const otherBases = baseTextSet instanceof Set ? baseTextSet : new Set(baseTextSet || []);
  const terms = collectSearchTerms(targetWord).filter((t) => t === targetText || !otherBases.has(t));
  if (!terms.length) return [];
  const subsMap = new Map();
  (mySubscriptions || []).forEach((s) => subsMap.set(s.target_id, s.starred));
  const cards = [];
  (databases || []).forEach((row) => {
    const ownerId = row.user_id;
    const ownerNickname = row.nickname || '';
    const isMine = ownerId === myId;
    const isSubscribed = subsMap.has(ownerId);
    const isStarred = subsMap.get(ownerId) === true;
    const data = row.data || {};
    walkDb(data.db, (w) => {
      if (!w) return;
      const isSameWord = normEq(w.text, targetText);
      const author = { ownerId, ownerNickname, isMine, isSubscribed, isStarred };
      const pushCard = (phrase, photo, level) => {
        if (!phrase && !photo) return;
        const phraseMatches = !!(phrase && phraseMatchesTerms(phrase, terms));
        if (!isSameWord && !phraseMatches) return;
        const addedAt = extractPhotoTimestamp(photo?.photoKey);
        cards.push({
          ownerId, ownerNickname, isMine, isSubscribed, isStarred,
          phrase: phrase || '', photo: photo || null, addedAt,
          kind: isSameWord ? 'same-word' : 'phrase-match', level: level || null,
          phraseAuthor: phrase ? author : null,
          photoAuthor: photo ? author : null,
        });
      };
      if (isMine) {
        // Своя база: свои примеры всегда видны мне.
        // Уровни самого целевого слова собираются в CategoryPanel отдельно;
        // здесь для своих слов ищем phrase-match по ВСЕМ уровням.
        if (isSameWord) return;
        LEVEL_ORDER.forEach((lv) => {
          const ph = getPhraseForLevel(w, lv) || '';
          if (!ph) return;
          pushCard(ph, getPhotoForLevel(w, lv) || null, lv);
        });
        return;
      }
      // Чужая база: только уровни, помеченные общими (shared), парой с одного уровня.
      const levels = getWordLevels(w);
      LEVEL_ORDER.forEach((lv) => {
        const slot = levels[lv];
        if (!slot || !slot.shared) return;
        pushCard(typeof slot.phrase === 'string' ? slot.phrase : '', slot.photo || null, lv);
      });
    });
  });
  // Сортировка: сначала по уровню (A1 -> A2 -> B1 -> B2), затем по времени добавления.
  const lvlRank = (c) => {
    const i = LEVEL_ORDER.indexOf(c.level);
    return i === -1 ? 99 : i;
  };
  cards.sort((a, b) => {
    const ra = lvlRank(a), rb = lvlRank(b);
    if (ra !== rb) return ra - rb;
    if (a.addedAt === 0 && b.addedAt !== 0) return 1;
    if (b.addedAt === 0 && a.addedAt !== 0) return -1;
    if (a.addedAt !== b.addedAt) return a.addedAt - b.addedAt;
    return String(a.ownerId || "").localeCompare(String(b.ownerId || ""));
  });
  return cards;
}
